import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import ActionToast from "../../../../shared/components/ActionToast";
import SettingsCard from "../shared/SettingsCard";
import SettingsFormField from "../shared/SettingsFormField";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../../shared/themes/redesignTokens";
import {
  getPricingDefaults,
  savePricingDefaults,
  clearPricingSuccess,
  clearPricingError,
} from "../../serviceProviderPricingSlice";


// Enum values matching backend
const WarrantyPeriodCode = { ThirtyDays: 1, NinetyDays: 2, SixMonths: 3, OneYear: 4, Custom: 5 };
const PaymentTermsCode = { PaymentOnCompletion: 1, Net30Days: 2, FiftyAdvanceFiftyCompletion: 3, Net60Days: 4, Custom: 5 };
const ValidityPeriodCode = { ThreeDays: 1, FiveDays: 2, SevenDays: 3, FourteenDays: 4, ThirtyDays: 5, Custom: 6 };

const inputSx = {
  width: "100%",
  padding: "10px 14px",
  border: `1.5px solid #E5E7EB`,
  borderRadius: dt.radiusSm,
  backgroundColor: dt.inputBg,
  fontFamily: dt.font,
  fontSize: "14px",
  color: dt.textPrimary,
  outline: "none",
  boxSizing: "border-box" as const,
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: dt.blue,
    boxShadow: `0 0 0 3px ${dt.blueGlow}`,
  },
};

const selectSx = {
  backgroundColor: dt.inputBg,
  borderRadius: dt.radiusSm,
  fontFamily: dt.font,
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: dt.blue,
    boxShadow: `0 0 0 3px ${dt.blueGlow}`,
  },
};

const SettingsPricing: React.FC = () => {
  const { translate } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const dispatch = useAppDispatch();
  const { settings, loading, saving, success, error } = useAppSelector(
    (state) => state.serviceProviderPricing
  );

  // Local form state
  const [hourlyRate, setHourlyRate] = useState("");
  const [travelRatePerKm, setTravelRatePerKm] = useState("");
  const [travelRatePerHour, setTravelRatePerHour] = useState("");
  const [warrantyPeriodCode, setWarrantyPeriodCode] = useState<number>(0);
  const [customWarranty, setCustomWarranty] = useState("");
  const [paymentTermsCode, setPaymentTermsCode] = useState<number>(0);
  const [customPaymentTerms, setCustomPaymentTerms] = useState("");
  const [validityPeriodCode, setValidityPeriodCode] = useState<number>(0);
  const [customValidity, setCustomValidity] = useState("");

  // Included services
  const [includeDiagnostics, setIncludeDiagnostics] = useState(false);
  const [includeLaborForRepair, setIncludeLaborForRepair] = useState(false);
  const [includePartsAndMaterials, setIncludePartsAndMaterials] = useState(false);
  const [includeTestingAndCalibration, setIncludeTestingAndCalibration] = useState(false);
  const [includeWorkAreaCleanup, setIncludeWorkAreaCleanup] = useState(false);
  const [includeDetailedServiceReport, setIncludeDetailedServiceReport] = useState(false);

  // Track loaded values for dirty detection
  const [loadedValues, setLoadedValues] = useState<string>("");

  // Fetch settings on mount
  useEffect(() => {
    dispatch(getPricingDefaults());
  }, [dispatch]);

  // Populate form when settings arrive
  useEffect(() => {
    if (settings) {
      const hr = settings.hourlyRate != null ? String(settings.hourlyRate) : "";
      const trKm = settings.travelRatePerKm != null ? String(settings.travelRatePerKm) : "";
      const trHr = settings.travelRatePerHour != null ? String(settings.travelRatePerHour) : "";
      const wpc = settings.warrantyPeriodCode ?? 0;
      const cw = settings.customWarrantyPeriod ?? "";
      const ptc = settings.paymentTermsCode ?? 0;
      const cpt = settings.customPaymentTerms ?? "";
      const vpc = settings.validityPeriodCode ?? 0;
      const cv = settings.customValidityPeriod ?? "";
      const diag = settings.includeDiagnostics ?? false;
      const labor = settings.includeLaborForRepair ?? false;
      const parts = settings.includePartsAndMaterials ?? false;
      const testing = settings.includeTestingAndCalibration ?? false;
      const cleanup = settings.includeWorkAreaCleanup ?? false;
      const report = settings.includeDetailedServiceReport ?? false;

      setHourlyRate(hr);
      setTravelRatePerKm(trKm);
      setTravelRatePerHour(trHr);
      setWarrantyPeriodCode(wpc);
      setCustomWarranty(cw);
      setPaymentTermsCode(ptc);
      setCustomPaymentTerms(cpt);
      setValidityPeriodCode(vpc);
      setCustomValidity(cv);
      setIncludeDiagnostics(diag);
      setIncludeLaborForRepair(labor);
      setIncludePartsAndMaterials(parts);
      setIncludeTestingAndCalibration(testing);
      setIncludeWorkAreaCleanup(cleanup);
      setIncludeDetailedServiceReport(report);

      setLoadedValues(
        JSON.stringify({ hr, trKm, trHr, wpc, cw, ptc, cpt, vpc, cv, diag, labor, parts, testing, cleanup, report })
      );
    }
  }, [settings]);

  // Dirty detection
  const currentValues = useMemo(
    () =>
      JSON.stringify({
        hr: hourlyRate,
        trKm: travelRatePerKm,
        trHr: travelRatePerHour,
        wpc: warrantyPeriodCode,
        cw: customWarranty,
        ptc: paymentTermsCode,
        cpt: customPaymentTerms,
        vpc: validityPeriodCode,
        cv: customValidity,
        diag: includeDiagnostics,
        labor: includeLaborForRepair,
        parts: includePartsAndMaterials,
        testing: includeTestingAndCalibration,
        cleanup: includeWorkAreaCleanup,
        report: includeDetailedServiceReport,
      }),
    [
      hourlyRate, travelRatePerKm, travelRatePerHour,
      warrantyPeriodCode, customWarranty, paymentTermsCode, customPaymentTerms,
      validityPeriodCode, customValidity,
      includeDiagnostics, includeLaborForRepair, includePartsAndMaterials,
      includeTestingAndCalibration, includeWorkAreaCleanup, includeDetailedServiceReport,
    ]
  );
  const isDirty = loadedValues !== "" && currentValues !== loadedValues;

  const handleSave = () => {
    dispatch(
      savePricingDefaults({
        serviceRadiusKm: settings?.serviceRadiusKm ?? null,
        baseLatitude: settings?.baseLatitude ?? null,
        baseLongitude: settings?.baseLongitude ?? null,
        hourlyRate: hourlyRate ? Number(hourlyRate) : null,
        travelRatePerKm: travelRatePerKm ? Number(travelRatePerKm) : null,
        travelRatePerHour: travelRatePerHour ? Number(travelRatePerHour) : null,
        warrantyPeriodCode: warrantyPeriodCode || null,
        customWarrantyPeriod: customWarranty || null,
        paymentTermsCode: paymentTermsCode || null,
        customPaymentTerms: customPaymentTerms || null,
        validityPeriodCode: validityPeriodCode || null,
        customValidityPeriod: customValidity || null,
        includeDiagnostics,
        includeLaborForRepair,
        includePartsAndMaterials,
        includeTestingAndCalibration,
        includeWorkAreaCleanup,
        includeDetailedServiceReport,
      })
    ).then((result) => {
      if (savePricingDefaults.fulfilled.match(result)) {
        // Update loaded values so the button becomes disabled again
        setLoadedValues(currentValues);
      }
    });
  };

  const checkboxSx = {
    color: dt.textMuted,
    "&.Mui-checked": { color: dt.blue },
  };
  const checkboxLabelSx = {
    "& .MuiFormControlLabel-label": {
      fontFamily: dt.font,
      fontSize: "13px",
      color: dt.textPrimary,
    },
  };

  const saveButton = (
    <Button
      variant="contained"
      onClick={handleSave}
      disabled={!isDirty || saving}
      sx={{
        background: isDirty
          ? accentGradientDark
          : "#E5E7EB",
        color: isDirty ? "#fff" : dt.textMuted,
        borderRadius: dt.radiusPill,
        padding: "8px 24px",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "13px",
        fontFamily: dt.font,
        boxShadow: isDirty ? "0 2px 8px rgba(52,152,219,0.25)" : "none",
        transition: "all 0.3s ease",
        whiteSpace: "nowrap",
        "&:hover": {
          background: isDirty
            ? accentGradientDarkHover
            : "#E5E7EB",
          transform: isDirty ? "translateY(-1px)" : "none",
          boxShadow: isDirty ? "0 4px 12px rgba(52,152,219,0.3)" : "none",
        },
        "&.Mui-disabled": {
          background: "#E5E7EB",
          color: dt.textMuted,
        },
      }}
    >
      {saving ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} sx={{ color: "#fff" }} />
          {translate("settings:pricing.saving")}
        </Box>
      ) : (
        tCommon("buttons.save")
      )}
    </Button>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <ActionToast
        open={!!success}
        variant="success"
        message={translate("settings:pricing.saveSuccess")}
        onClose={() => dispatch(clearPricingSuccess())}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={translate("settings:pricing.saveError")}
        onClose={() => dispatch(clearPricingError())}
        duration={6000}
      />

      {/* Labor Rates Card */}
      <SettingsCard
        title={translate("settings:pricing.laborRates")}
        subtitle={translate("settings:pricing.laborRatesDescription")}
        action={saveButton}
        animationDelay="0.15s"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "16px",
          }}
        >
          <SettingsFormField label={translate("settings:pricing.hourlyRate")}>
            <Box
              component="input"
              type="number"
              value={hourlyRate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHourlyRate(e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.01"
              sx={inputSx}
            />
            <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font, mt: "4px" }}>
              EUR/hour
            </Box>
          </SettingsFormField>

          <SettingsFormField label={translate("settings:pricing.currency")}>
            <Select
              value="EUR"
              disabled
              size="small"
              fullWidth
              sx={selectSx}
            >
              <MenuItem value="EUR">EUR</MenuItem>
            </Select>
          </SettingsFormField>
        </Box>
      </SettingsCard>

      {/* Travel Costs Card */}
      <SettingsCard
        title={translate("settings:pricing.travelCosts")}
        subtitle={translate("settings:pricing.travelCostsDescription")}
        animationDelay="0.2s"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "16px",
          }}
        >
          <SettingsFormField label={translate("settings:pricing.ratePerKm")}>
            <Box
              component="input"
              type="number"
              value={travelRatePerKm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTravelRatePerKm(e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.01"
              sx={inputSx}
            />
            <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font, mt: "4px" }}>
              EUR/km
            </Box>
          </SettingsFormField>

          <SettingsFormField label={translate("settings:pricing.ratePerHourTravel")}>
            <Box
              component="input"
              type="number"
              value={travelRatePerHour}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTravelRatePerHour(e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.01"
              sx={inputSx}
            />
            <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font, mt: "4px" }}>
              EUR/hour
            </Box>
          </SettingsFormField>
        </Box>
      </SettingsCard>

      {/* Warranty & Terms Card */}
      <SettingsCard
        title={translate("settings:pricing.warrantyAndTerms")}
        animationDelay="0.25s"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Default Warranty Period */}
          <Box>
            <SettingsFormField label={translate("settings:pricing.defaultWarranty")}>
              <Select
                value={warrantyPeriodCode}
                onChange={(e: SelectChangeEvent<number>) => setWarrantyPeriodCode(Number(e.target.value))}
                size="small"
                fullWidth
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value={0}><em>--</em></MenuItem>
                <MenuItem value={WarrantyPeriodCode.ThirtyDays}>{translate("settings:pricing.warranty30days")}</MenuItem>
                <MenuItem value={WarrantyPeriodCode.NinetyDays}>{translate("settings:pricing.warranty90days")}</MenuItem>
                <MenuItem value={WarrantyPeriodCode.SixMonths}>{translate("settings:pricing.warranty6months")}</MenuItem>
                <MenuItem value={WarrantyPeriodCode.OneYear}>{translate("settings:pricing.warranty1year")}</MenuItem>
                <MenuItem value={WarrantyPeriodCode.Custom}>{translate("settings:pricing.warrantyCustom")}</MenuItem>
              </Select>
            </SettingsFormField>
            {warrantyPeriodCode === WarrantyPeriodCode.Custom && (
              <Box sx={{ mt: 1.5 }}>
                <SettingsFormField label={translate("settings:pricing.customWarranty")}>
                  <Box
                    component="input"
                    value={customWarranty}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomWarranty(e.target.value)}
                    placeholder="e.g. 45 days, until next scheduled maintenance"
                    sx={inputSx}
                  />
                </SettingsFormField>
              </Box>
            )}
          </Box>

          {/* Default Payment Terms */}
          <Box>
            <SettingsFormField label={translate("settings:pricing.defaultPaymentTerms")}>
              <Select
                value={paymentTermsCode}
                onChange={(e: SelectChangeEvent<number>) => setPaymentTermsCode(Number(e.target.value))}
                size="small"
                fullWidth
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value={0}><em>--</em></MenuItem>
                <MenuItem value={PaymentTermsCode.PaymentOnCompletion}>{translate("settings:pricing.paymentOnCompletion")}</MenuItem>
                <MenuItem value={PaymentTermsCode.Net30Days}>{translate("settings:pricing.paymentNet30")}</MenuItem>
                <MenuItem value={PaymentTermsCode.FiftyAdvanceFiftyCompletion}>{translate("settings:pricing.payment5050")}</MenuItem>
                <MenuItem value={PaymentTermsCode.Net60Days}>{translate("settings:pricing.paymentNet60")}</MenuItem>
                <MenuItem value={PaymentTermsCode.Custom}>{translate("settings:pricing.paymentCustom")}</MenuItem>
              </Select>
            </SettingsFormField>
            {paymentTermsCode === PaymentTermsCode.Custom && (
              <Box sx={{ mt: 1.5 }}>
                <SettingsFormField label={translate("settings:pricing.customPaymentTerms")}>
                  <Box
                    component="input"
                    value={customPaymentTerms}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomPaymentTerms(e.target.value)}
                    placeholder="e.g. 40% advance, 60% after inspection"
                    sx={inputSx}
                  />
                </SettingsFormField>
              </Box>
            )}
          </Box>

          {/* Default Offer Validity */}
          <Box>
            <SettingsFormField label={translate("settings:pricing.defaultValidity")}>
              <Select
                value={validityPeriodCode}
                onChange={(e: SelectChangeEvent<number>) => setValidityPeriodCode(Number(e.target.value))}
                size="small"
                fullWidth
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value={0}><em>--</em></MenuItem>
                <MenuItem value={ValidityPeriodCode.ThreeDays}>{translate("settings:pricing.validity3days")}</MenuItem>
                <MenuItem value={ValidityPeriodCode.FiveDays}>{translate("settings:pricing.validity5days")}</MenuItem>
                <MenuItem value={ValidityPeriodCode.SevenDays}>{translate("settings:pricing.validity7days")}</MenuItem>
                <MenuItem value={ValidityPeriodCode.FourteenDays}>{translate("settings:pricing.validity14days")}</MenuItem>
                <MenuItem value={ValidityPeriodCode.ThirtyDays}>{translate("settings:pricing.validity30days")}</MenuItem>
                <MenuItem value={ValidityPeriodCode.Custom}>{translate("settings:pricing.validityCustom")}</MenuItem>
              </Select>
            </SettingsFormField>
            {validityPeriodCode === ValidityPeriodCode.Custom && (
              <Box sx={{ mt: 1.5 }}>
                <SettingsFormField label={translate("settings:pricing.customValidity")}>
                  <Box
                    component="input"
                    value={customValidity}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomValidity(e.target.value)}
                    placeholder="e.g. Valid until project start date"
                    sx={inputSx}
                  />
                </SettingsFormField>
              </Box>
            )}
          </Box>
        </Box>
      </SettingsCard>

      {/* Included Services Card */}
      <SettingsCard
        title={translate("settings:pricing.includedServices")}
        subtitle={translate("settings:pricing.includedServicesDescription")}
        animationDelay="0.3s"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <FormControlLabel
            control={<Checkbox checked={includeDiagnostics} onChange={(e) => setIncludeDiagnostics(e.target.checked)} sx={checkboxSx} />}
            label={translate("settings:pricing.diagnostics")}
            sx={checkboxLabelSx}
          />
          <FormControlLabel
            control={<Checkbox checked={includeLaborForRepair} onChange={(e) => setIncludeLaborForRepair(e.target.checked)} sx={checkboxSx} />}
            label={translate("settings:pricing.laborForRepair")}
            sx={checkboxLabelSx}
          />
          <FormControlLabel
            control={<Checkbox checked={includePartsAndMaterials} onChange={(e) => setIncludePartsAndMaterials(e.target.checked)} sx={checkboxSx} />}
            label={translate("settings:pricing.partsAndMaterials")}
            sx={checkboxLabelSx}
          />
          <FormControlLabel
            control={<Checkbox checked={includeTestingAndCalibration} onChange={(e) => setIncludeTestingAndCalibration(e.target.checked)} sx={checkboxSx} />}
            label={translate("settings:pricing.testingAndCalibration")}
            sx={checkboxLabelSx}
          />
          <FormControlLabel
            control={<Checkbox checked={includeWorkAreaCleanup} onChange={(e) => setIncludeWorkAreaCleanup(e.target.checked)} sx={checkboxSx} />}
            label={translate("settings:pricing.workAreaCleanup")}
            sx={checkboxLabelSx}
          />
          <FormControlLabel
            control={<Checkbox checked={includeDetailedServiceReport} onChange={(e) => setIncludeDetailedServiceReport(e.target.checked)} sx={checkboxSx} />}
            label={translate("settings:pricing.detailedServiceReport")}
            sx={checkboxLabelSx}
          />
        </Box>
      </SettingsCard>

      {/* Invoicing (Future) Card */}
      <SettingsCard title={translate("settings:pricing.invoicing")} animationDelay="0.35s">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: dt.textMuted,
              fontFamily: dt.font,
              backgroundColor: "#F4F6F9",
              padding: "6px 16px",
              borderRadius: "10px",
            }}
          >
            {translate("settings:pricing.comingSoon")}
          </Box>
        </Box>
      </SettingsCard>

    </Box>
  );
};

export default SettingsPricing;
